import { PropertiesPanel } from '../../../../../widgets/propertiesPanel';
import { Spinner } from '../../../../../widgets/spinner';
import { useDialogManager } from '../../../../../widgets/dialog';
import { debounce } from 'lodash';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useMaterialsPropertiesCreator } from 'src/components/pages/preview3D/editors/editMaterialsEditor/editMaterialsEditorConfig';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import UpgradeDialog from 'src/components/widgets/upgradeDialog/upgradeDialog';
import { stringFormat, T } from 'src/shared/helpers';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import styled from 'styled-components';

const SidebarContainer = styled.div`
    display: flex;
    flex-direction: column;
    max-height: 100%;
    gap: 10px;
`;

const SidebarPropertiesPanel = styled.div`
    flex: 1;
    overflow-y: auto;
`;

const Title = styled.h3`
    display: flex;
    align-items: center;
    gap: 5px;

    &:first-child {
        margin-top: 0;
    }

    margin-bottom: 20px;
`;

const generateTextureUniqueId = <T extends BABYLON.Material>(material: T, type: string, mobile: boolean) => {
    return [material.id, type, mobile ? 'mobile' : 'default'].join('_');
};

const ensureAbsolute = (rootUrl: string, url: string) => {
    if (!/^(?:\/|[a-z]+:\/\/)/.test(url)) {
        return rootUrl + url;
    }
    return url;
};

interface MaterialOverlaySimpleProps {
    material: any; // Il materiale BABYLON selezionato
    onMaterialUpdated?: (material: any) => void; // Callback per notificare aggiornamenti del materiale
    onNameChanged?: (newName: string) => void; // Callback per notificare cambio nome specificatamente
    additionalFeatures?: any;
    onClose?: () => void; // Callback per chiudere l'overlay
}

const UpgradeBanner = styled(UpgradeDialog)`
    margin-bottom: 20px;
    p {
        margin: 0;
        padding: 0;
        line-height: 18px;
    }
    z-index: 10000 !important;
`;

const MaterialOverlaySimple: React.FC<MaterialOverlaySimpleProps> = ({ material, onMaterialUpdated, onNameChanged, additionalFeatures, onClose }) => {
    const [zkProduct, zkScene, sceneManager, sceneEditor, designManager] = usePreview3DStore((x) => [
        x.zkProduct,
        x.zkScene,
        x.sceneManager,
        x.sceneEditor,
        x.designManager
    ]);

    // Spostato getTextureUrls dentro il componente per accedere a zkScene
    const getTextureUrls = useCallback(
        (texture: BABYLON.BaseTexture): string[] => {
            const isTextureWithFiles = (texture: BABYLON.BaseTexture): texture is BABYLON.BaseTexture & { _files: string[] } =>
                '_files' in texture && !!texture._files && Array.isArray(texture._files);

            const isTextureWithUrl = (texture: BABYLON.BaseTexture): texture is BABYLON.BaseTexture & { url: string } => 'url' in texture && !!texture.url;

            if (isTextureWithFiles(texture) && texture._files.length > 1) {
                return texture._files;
            }
            if (texture.metadata && texture.metadata.fileUrl) {
                return [texture.metadata.fileUrl];
            }
            if (isTextureWithUrl(texture) && texture.url.startsWith('data:')) {
                return [texture.url];
            }
            if (texture.getInternalTexture && texture.getInternalTexture()?.url) {
                const textureUrl = texture.getInternalTexture()?.url;
                return textureUrl ? [textureUrl] : [];
            }
            if (isTextureWithUrl(texture) && texture.url) {
                console.log("sono nell'if");
                // Check if materialMapping exists before accessing its properties
                if (
                    (texture as any).materialMapping &&
                    (texture as any).materialMapping.libraryMaterialID &&
                    (texture as any).materialMapping.libraryMaterialRootUrl
                ) {
                    const materialMapping = (texture as any).materialMapping;
                    const idx = texture.url.indexOf(materialMapping.libraryMaterialID.replace(/-/g, ''));
                    if (idx !== -1) return [materialMapping.libraryMaterialRootUrl + texture.url];
                }
                return [ensureAbsolute(zkScene?.get('rootUrl') || '', texture.url)];
            }
            return [];
        },
        [zkScene]
    );

    const [materialsPreviews, setMaterialsPreviews] = useState<Map<string, string>>(new Map());
    const [loadingTextures, setLoadingTextures] = useState<Record<string, boolean>>({});
    const [, setUpdate] = useState(false);
    const materialsPropertiesCreator = useMaterialsPropertiesCreator();
    const { showDialog, closeDialog } = useDialogManager();
    const { ecommerceName, sellerMarketplace, updateSubscription } = useSubscriptionData();

    const materialsPreviewsRef = useRef(materialsPreviews);
    materialsPreviewsRef.current = materialsPreviews;

    const renderDebounced = useCallback(
        debounce(() => designManager?.render(), 100),
        [designManager]
    );

    const updateMaterialsPreviews = useCallback(
        debounce(async (materials?: BABYLON.Material[]) => {
            // TODO: Implementare preview se necessario
        }, 500),
        []
    );

    const updateState = useCallback(
        <T extends BABYLON.Material>(material: T, refreshMaterials?: boolean) => {
            const isDesignMaterial = zkProduct
                ?.get('scenes')
                .at(0)
                .get('meshSides')
                ?.toArray()
                .some((meshSide) => meshSide.get('materialID') === material.id);

            // Always re render because the base design material uses the same metallic/roughness of the base material
            if (isDesignMaterial) {
                renderDebounced();
            }

            setUpdate((prev) => !prev);
            sceneEditor?.render();

            updateMaterialsPreviews([material]);
        },
        [sceneManager, zkProduct, renderDebounced, sceneEditor, updateMaterialsPreviews]
    );

    const isTextureLoading = useCallback(
        <T extends BABYLON.Material>(material: T, type: string, mobile: boolean) => {
            return loadingTextures[generateTextureUniqueId(material, type, mobile)];
        },
        [loadingTextures]
    );

    const uploadTexture = useCallback(
        async <T extends BABYLON.Material>(material: T, type: string, mobile: boolean, files: FileList) => {
            console.log('sono upload texture', material, type, mobile, files, additionalFeatures);
            if (!sceneEditor) {
                throw new Error('Scene editor is not available');
            }

            if (files.length === 0) {
                throw new Error('No files selected');
            }

            if (type === 'reflection' && !additionalFeatures?.customEnvironmentTexture) {
                // Chiudo l'overlay prima di mostrare la dialog
                if (onClose) {
                    onClose();
                }

                showDialog(
                    'Upgrade_Plan',
                    <UpgradeBanner
                        marketPlace={sellerMarketplace ?? undefined}
                        message={stringFormat(T._('Your current plan does not include this feature. Upgrade now to access it.', 'Admin'), 'Grow')}
                        onUpgradeCompleted={async () => {
                            updateSubscription();
                        }}
                    />
                );
                return;
            }

            const uniqueId = generateTextureUniqueId(material, type, mobile);
            setLoadingTextures((prev) => ({ ...prev, [uniqueId]: true }));

            try {
                if (mobile) {
                    await sceneEditor.loadMobileTextureFilesAsync(Array.from(files), material, type, false);
                } else {
                    await sceneEditor.loadTextureFilesAsync(Array.from(files), material, type, false);
                }
            } catch (error) {
                console.error(error);
                throw new Error('Failed to upload texture', {
                    cause: error
                });
            } finally {
                setLoadingTextures((prev) => ({ ...prev, [uniqueId]: false }));
                updateState(material);
            }
        },
        [sceneEditor, updateState]
    );

    const removeTexture = useCallback(
        async <T extends BABYLON.Material>(material: T, type: string, mobile: boolean) => {
            if (!sceneEditor) {
                throw new Error('Scene editor is not available');
            }

            if (mobile) {
                sceneEditor.removeMaterialMobileTexture(material, type);
            } else {
                sceneEditor.removeMaterialTexture(material, type);
            }

            updateState(material);
        },
        [sceneEditor, updateState]
    );

    const getTexturePreviewUrl = useCallback(
        <T extends BABYLON.Material>(material: T, type: string, mobile: boolean) => {
            if (!sceneEditor || !zkScene) {
                return null;
            }
            console.log('material e type texture', material, type);
            const texture = sceneEditor.getMaterialTexture(material, type);

            console.log('gettexturepreivew , texture: ', texture);
            if (!texture) {
                return null;
            }

            // mi costruisco prima l'url root per le mobile
            // al momento l'url mobile di materiali importati da scena è rotto perchè è prima da fixare:
            // https://www.wrike.com/open.htm?id=1712281262
            let materialsRootUrl = zkScene.get('rootUrl') || '';

            // if (
            // 	(texture as any).materialMapping &&
            // 	(texture as any).materialMapping.libraryMaterialID &&
            // 	(texture as any).materialMapping.libraryMaterialRootUrl
            // ) {
            // 	const materialMapping = (texture as any).materialMapping;
            // 	const textureUrl = (texture as any).url;
            // 	const idx = textureUrl && typeof textureUrl === 'string' ? textureUrl.indexOf(materialMapping.libraryMaterialID.replace(/-/g, '')) : -1;
            // 	if (idx !== -1 && textureUrl) {
            // 		materialsRootUrl = materialMapping.libraryMaterialRootUrl + textureUrl;
            // 	}
            // }
            return !mobile
                ? getTextureUrls(texture)[0]
                : texture.metadata &&
                      texture.metadata.mobileUrl &&
                      ((!texture.metadata.mobileUrl.startsWith('http') ? materialsRootUrl + texture.metadata.mobileUrl : texture.metadata.mobileUrl) as string);
        },
        [sceneEditor, zkScene]
    );

    // Funzione stabile per l'onChange del nome materiale
    const handleMaterialNameChange = useCallback(
        (newValue: string) => {
            if (!material) {
                return;
            }

            // Aggiorna il nome direttamente sul materiale BABYLON
            material.name = newValue || 'Material';

            // Chiama updateState per aggiornare l'UI dell'overlay
            updateState(material);

            // Notifica immediatamente il cambiamento del nome al parent
            if (onNameChanged) {
                onNameChanged(material.name);
            }

            // Notifica il sistema che i materiali sono stati aggiornati per sincronizzare l'albero
            if (onMaterialUpdated) {
                setTimeout(() => {
                    onMaterialUpdated(material);
                }, 50); // Small delay to ensure material name is fully updated
            }
        },
        [material, updateState, onMaterialUpdated, onNameChanged]
    );

    // Helpers per il materialsPropertiesCreator
    const helpers = useMemo(() => {
        if (!sceneEditor || !sceneManager) {
            return null;
        }
        return {
            sceneEditor,
            sceneManager,
            updateState,
            isTextureLoading,
            uploadTexture,
            removeTexture,
            getTexturePreviewUrl,
            handleMaterialNameChange
        };
    }, [sceneEditor, sceneManager, updateState, isTextureLoading, uploadTexture, removeTexture, getTexturePreviewUrl, handleMaterialNameChange]);

    // Funzione per verificare se il materiale è Standard (non supportato)
    const isStandardMaterial = useCallback((material: any) => {
        // Un materiale Standard ha diffuseColor ma NON ha proprietà PBR avanzate
        return (
            material &&
            material.diffuseColor !== undefined &&
            (material.clearCoat === undefined || !material.clearCoat?.isEnabled) &&
            (material.metallic === undefined || material.metallic === 0) &&
            (material.roughness === undefined || material.roughness === 0) &&
            // Oppure verifica il tipo direttamente se disponibile
            (material instanceof BABYLON?.StandardMaterial || material.getClassName?.() === 'StandardMaterial')
        );
    }, []);

    if (!material || !helpers) {
        return (
            <SidebarContainer>
                <Title>{T._('Material Properties', 'Admin')}</Title>
                <Spinner />
            </SidebarContainer>
        );
    }

    // Se è un materiale Standard, mostra solo il messaggio di non supportato
    if (isStandardMaterial(material)) {
        return (
            <SidebarContainer>
                <Title>
                    <span>{T._('Material Properties', 'Admin')}</span>
                </Title>
                <SidebarPropertiesPanel>
                    <div
                        style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#666',
                            fontStyle: 'italic'
                        }}
                    >
                        {T._('Materiale non supportato', 'Admin')}
                    </div>
                </SidebarPropertiesPanel>
            </SidebarContainer>
        );
    }

    // Renderizza esattamente come nel file editMaterialsEditor per materiali PBR
    console.log('sono material', material);
    return (
        <SidebarContainer>
            <Title>
                <span>{T._('Material Properties', 'Admin')}</span>
            </Title>
            <SidebarPropertiesPanel>
                <PropertiesPanel
                    properties={[material]
                        .sort((a, b) => a.name.localeCompare(b.name)) // Sort materials by name
                        .map((material) => {
                            const properties = materialsPropertiesCreator(material, helpers);
                            return {
                                type: 'group',
                                id: material.id,
                                header: (
                                    <>
                                        {materialsPreviews.get(material.id) ? <img src={materialsPreviews.get(material.id)} width='48' /> : null}{' '}
                                        {material.name}
                                    </>
                                ),
                                defaultIsOpen: true, // Sempre aperto per singolo materiale
                                properties: properties
                            };
                        })}
                />
            </SidebarPropertiesPanel>
        </SidebarContainer>
    );
};

export default MaterialOverlaySimple;
